import { CheckCircleOutline, Close, QrCodeScanner, Refresh, Search } from "@mui/icons-material";
import { Alert, Box, Button, CircularProgress, Stack, TextField, Typography } from "@mui/material";
import { Html5QrcodeScanner, Html5QrcodeScannerState } from "html5-qrcode";
import CookieJs from "js-cookie";
import { FormEvent, useEffect, useId, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import MainLayout from "../components/MainLayout";
import ProtectedPermissionPage from "../components/ProtectedPermissionPage";
import { useAppSelector } from "../hooks/useSelector";
import eventService from "../services/event";
import scan, { RedeemTicketDetails } from "../services/scan";
import { formatTicketDoor } from "../utils/ticket";

type Phase = "ticket" | "checking" | "preview" | "wristband" | "confirming" | "success" | "error" | "mismatch" | "uncertain";
let cameraLifecycle: Promise<void> = Promise.resolve();

function RedeemCamera({ active, onScan, onError }: { active: boolean; onScan: (value: string) => void; onError: (message: string) => void }) {
  const regionId = `double-redeem-${useId().replace(/:/g, "")}`;
  const scanner = useRef<Html5QrcodeScanner | null>(null);
  const current = useRef({ active, onScan, onError });
  current.current = { active, onScan, onError };

  useEffect(() => {
    let disposed = false;
    let instance: Html5QrcodeScanner | null = null;
    cameraLifecycle = cameraLifecycle.then(() => {
      if (disposed) return;
      instance = new Html5QrcodeScanner(regionId, { fps: 10, qrbox: { width: 250, height: 250 } }, false);
      scanner.current = instance;
      instance.render(value => {
        if (!current.current.active || disposed) return;
        try {
          if (instance?.getState() === Html5QrcodeScannerState.SCANNING) instance.pause(true);
        } catch {
          current.current.onError("Camera could not pause. You can use manual entry.");
        }
        current.current.onScan(value);
      }, () => {});
    }).catch(() => {
      if (!disposed) current.current.onError("Camera unavailable. You can use manual entry.");
    });
    return () => {
      disposed = true;
      scanner.current = null;
      cameraLifecycle = cameraLifecycle.then(async () => {
        if (instance) await instance.clear();
      }).catch(() => {});
    };
  }, [regionId]);

  useEffect(() => {
    const instance = scanner.current;
    if (!instance) return;
    try {
      const state = instance.getState();
      if (active && state === Html5QrcodeScannerState.PAUSED) instance.resume();
      if (!active && state === Html5QrcodeScannerState.SCANNING) instance.pause(true);
    } catch {
      current.current.onError("Camera unavailable. You can use manual entry.");
    }
  }, [active]);

  return <Box id={regionId} sx={{ width: "100%", maxWidth: 480, mx: "auto", overflow: "hidden" }} />;
}

function displayValue(value: string | null | undefined) {
  return !value || value === "null" ? "-" : value;
}

function TicketDetails({ ticket }: { ticket: RedeemTicketDetails }) {
  const details = [
    ["TicketID", ticket.ticket_id], ["Name", ticket.name], ["Email", ticket.email],
    ["Pax", String(ticket.ticket_count)], ["Category", ticket.category],
    ["Wristband", ticket.wristbands.map(wristband => wristband.unique_id).join(", ")],
    ["Seat Number", ticket.seat_number],
    ["Door", formatTicketDoor(ticket.door)],
  ];
  return (
    <Box component="dl" sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "120px minmax(0, 1fr)" }, gap: 1, m: 0 }}>
      {details.map(([label, value]) => (
        <Box key={label} sx={{ display: "contents" }}>
          <Typography component="dt" color="text.secondary">{label}</Typography>
          <Typography component="dd" sx={{ m: 0, overflowWrap: "anywhere" }}>{displayValue(value)}</Typography>
        </Box>
      ))}
    </Box>
  );
}

function DoubleRedeemFlow() {
  const cookieUser = JSON.parse(CookieJs.get("USER") || "{}");
  const currentUser = useAppSelector(state => state.global.user);
  const eventId = Number(currentUser?.event_id ?? cookieUser.event_id);
  const userId = currentUser?.id ?? cookieUser.id;
  const [phase, setPhase] = useState<Phase>("ticket");
  const [ticket, setTicket] = useState<RedeemTicketDetails | null>(null);
  const [entry, setEntry] = useState("");
  const [message, setMessage] = useState("");
  const [cameraError, setCameraError] = useState("");
  const [eventName, setEventName] = useState("");
  const pending = useRef(false);
  const generation = useRef(0);
  const latest = useRef({ phase, ticket, eventId });
  latest.current = { phase, ticket, eventId };

  const changePhase = (next: Phase) => {
    latest.current.phase = next;
    setPhase(next);
  };

  const reset = () => {
    generation.current += 1;
    pending.current = false;
    latest.current.ticket = null;
    setTicket(null);
    setEntry("");
    setMessage("");
    changePhase("ticket");
  };

  useEffect(() => {
    generation.current += 1;
    pending.current = false;
    latest.current.ticket = null;
    latest.current.phase = "ticket";
    setTicket(null);
    setEntry("");
    setMessage("");
    setPhase("ticket");
    setEventName("");
    let disposed = false;
    if (eventId > 0) {
      eventService.viewEventDetail(eventId).then(response => {
        if (!disposed) setEventName(response?.data?.name || "");
      }).catch(() => {});
    }
    return () => {
      disposed = true;
      generation.current += 1;
    };
  }, [eventId, userId]);

  const submitScan = async (value: string, recheck = false) => {
    const current = latest.current;
    if (pending.current || (!recheck && !["ticket", "wristband"].includes(current.phase)) || !value.trim()) return;
    if (!Number.isInteger(current.eventId) || current.eventId <= 0) {
      setMessage("Select a valid event before scanning.");
      changePhase("error");
      return;
    }
    const confirming = current.phase === "wristband" && !recheck;
    const ticketId = confirming || recheck ? current.ticket?.ticket_id : value;
    if (!ticketId) return;
    pending.current = true;
    const requestGeneration = ++generation.current;
    changePhase(confirming ? "confirming" : "checking");
    setMessage("");
    setEntry("");
    try {
      const response = confirming
        ? await scan.postDoubleRedeem({ ticketId, eventId: current.eventId, wristbandId: value })
        : await scan.checkRedeemTicket({ ticketId, eventId: current.eventId });
      if (requestGeneration !== generation.current) return;
      setTicket(response.data);
      latest.current.ticket = response.data;
      if (confirming) setMessage(response.message);
      changePhase(confirming ? "success" : "preview");
      const audio = new Audio("/beep.mp3");
      void audio.play().catch(() => {});
    } catch (error: any) {
      if (requestGeneration !== generation.current) return;
      const result = error?.response?.data;
      if (confirming && (!result?.code || result.code === "INTERNAL_ERROR")) {
        setMessage("Redemption status is uncertain. Check ticket status before trying again.");
        changePhase("uncertain");
      } else {
        setMessage(result?.error || "Unable to check ticket. Please try again.");
        const retryCodes = ["WRISTBAND_MISMATCH", "WRISTBAND_NOT_FOUND", "INVALID_WRISTBAND", "INVALID_INPUT", "AMBIGUOUS_WRISTBAND"];
        changePhase(confirming && retryCodes.includes(result?.code) ? "mismatch" : "error");
      }
    } finally {
      if (requestGeneration === generation.current) pending.current = false;
    }
  };

  const startWristband = () => {
    setMessage("");
    setEntry("");
    changePhase("wristband");
  };
  const onManualSubmit = (event: FormEvent) => {
    event.preventDefault();
    void submitScan(entry);
  };
  const scanning = phase === "ticket" || phase === "wristband";
  const busy = phase === "checking" || phase === "confirming";

  return (
    <Stack spacing={3} sx={{ maxWidth: 860, mx: "auto", minWidth: 0 }}>
      <Box>
        <Typography variant="h5" component="h1">Double Redeem</Typography>
        <Typography color="text.secondary">{eventName}</Typography>
      </Box>
      {ticket && <TicketDetails ticket={ticket} />}
      {message && <Alert severity={phase === "success" ? "success" : phase === "uncertain" ? "warning" : "error"}>{message}</Alert>}
      {cameraError && scanning && <Alert severity="warning" onClose={() => setCameraError("")}>{cameraError}</Alert>}
      {busy && <Stack direction="row" spacing={1} alignItems="center" role="status"><CircularProgress size={20} /><Typography>{phase === "checking" ? "Checking ticket..." : "Confirming redemption..."}</Typography></Stack>}
      <Box sx={{ display: scanning ? "block" : "none" }} aria-hidden={!scanning}>
        <RedeemCamera active={scanning} onScan={value => { void submitScan(value); }} onError={setCameraError} />
      </Box>
      {scanning && (
        <Box component="form" onSubmit={onManualSubmit}>
          <Stack spacing={2} direction={{ xs: "column", sm: "row" }}>
            <TextField fullWidth label={phase === "ticket" ? "Ticket ID" : "Wristband ID"} value={entry} onChange={event => setEntry(event.target.value)} inputProps={{ maxLength: 4096 }} autoComplete="off" />
            <Button type="submit" variant="contained" startIcon={phase === "ticket" ? <Search /> : <CheckCircleOutline />} disabled={!entry.trim()} sx={{ flexShrink: 0 }}>
              {phase === "ticket" ? "Check Ticket" : "Confirm Wristband"}
            </Button>
          </Stack>
        </Box>
      )}
      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
        {phase === "preview" && <Button variant="contained" startIcon={<QrCodeScanner />} onClick={startWristband}>Scan Wristband</Button>}
        {phase === "mismatch" && <Button variant="contained" startIcon={<Refresh />} onClick={startWristband}>Retry Wristband</Button>}
        {phase === "uncertain" && <Button variant="contained" startIcon={<Search />} onClick={() => { void submitScan(ticket!.ticket_id, true); }}>Check Ticket Status</Button>}
        {phase !== "ticket" && <Button startIcon={phase === "success" || phase === "error" ? <QrCodeScanner /> : <Close />} onClick={reset} disabled={phase === "confirming"}>
          {phase === "success" ? "Next Ticket" : phase === "error" ? "Scan Another Ticket" : "Cancel"}
        </Button>}
      </Stack>
    </Stack>
  );
}

export default function DoubleRedeem() {
  return (
    <MainLayout>
      <Helmet><title>Double Redeem</title></Helmet>
      <ProtectedPermissionPage acceptPermissions={[1, 2]}><DoubleRedeemFlow /></ProtectedPermissionPage>
    </MainLayout>
  );
}